import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { AircraftEntity, FlightOperator } from "@dtm-frontend/shared/ui";
import { LocalComponentStore } from "@dtm-frontend/shared/utils";
import { UntilDestroy } from "@ngneat/until-destroy";
import { Cartesian3 } from "cesium";
import { combineLatestWith } from "rxjs";
import { map } from "rxjs/operators";
import { MapUtils } from "../../index";

interface FlightTrackerDetailsComponentState {
    aircraftEntity: AircraftEntity | undefined;
    isOperatorVisible: boolean;
}

@UntilDestroy()
@Component({
    selector: "dtm-map-flight-tracker-aircraft-details",
    templateUrl: "./flight-tracker-aircraft-details.component.html",
    styleUrls: ["./flight-tracker-aircraft-details.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LocalComponentStore],
})
export class FlightTrackerAircraftDetailsComponent {
    @Input() public set aircraftEntity(value: AircraftEntity | undefined) {
        this.localStore.patchState({ aircraftEntity: value });
    }
    @Output() public readonly detailsClose = new EventEmitter<void>();

    protected readonly aircraftEntity$ = this.localStore.selectByKey("aircraftEntity");
    protected readonly isOperatorVisible$ = this.localStore.selectByKey("isOperatorVisible");
    protected readonly operatorPosition$ = this.aircraftEntity$.pipe(
        combineLatestWith(this.isOperatorVisible$),
        map(([aircraftEntity, isOperatorVisible]) =>
            this.transformRemoteIdDataToOperatorPosition(isOperatorVisible ? aircraftEntity?.operator : undefined)
        )
    );

    constructor(private readonly localStore: LocalComponentStore<FlightTrackerDetailsComponentState>) {
        this.localStore.setState({ aircraftEntity: undefined, isOperatorVisible: false });
    }

    protected getAccuracy(accuracy: AircraftEntity["position"]["accuracy"]): string | undefined {
        if (!accuracy.latitude || !accuracy.longitude) {
            return;
        }

        return Math.max(accuracy.latitude, accuracy.longitude).toFixed(0);
    }

    protected closeDetails(): void {
        this.detailsClose.emit();
        this.localStore.setState({ aircraftEntity: undefined, isOperatorVisible: false });
    }

    protected toggleOperatorVisibility(): void {
        this.localStore.patchState(({ isOperatorVisible }) => ({ isOperatorVisible: !isOperatorVisible }));
    }

    private transformRemoteIdDataToOperatorPosition(operator?: FlightOperator): Cartesian3 | undefined {
        if (!operator) {
            return;
        }

        return MapUtils.convertSerializableCartographicToCartesian3({
            latitude: operator.latitude,
            longitude: operator.longitude,
        });
    }
}
